/**
 * Settings scope derivation (ENT-3705).
 *
 * Maps the viewer's existing seat signals to the organisation-settings home that the Admin nav
 * group and command palette label from. Pure function — no data fetching; callers pass the signals
 * they already hold. See the D1 scope table on ENT-3705.
 */

export type SettingsScopeKind =
  | 'msp_home'
  | 'managed_customer'
  | 'customer'
  | 'supplier_portal';

export interface SettingsScopeSignals {
  /** Active org is an MSP's own org (the viewer is MSP staff). */
  isMSP: boolean;
  /** MSP staff are currently acting inside one of their managed customer orgs. */
  isViewingManagedCustomer: boolean;
  /** The viewer is in the supplier portal shell (VendorLayout). */
  isSupplierPortal: boolean;
}

export interface SettingsScope {
  kind: SettingsScopeKind;
  /** Admin nav label + settings page title. */
  label: string;
  /** Route the organisation-settings item points at. */
  route: string;
}

const SCOPE_LABELS: Record<SettingsScopeKind, string> = {
  msp_home: 'MSP Settings',
  managed_customer: 'Customer Settings',
  customer: 'Organisation Settings',
  supplier_portal: 'Supplier Settings',
};

const SCOPE_ROUTES: Record<SettingsScopeKind, string> = {
  msp_home: '/settings/organisation',
  managed_customer: '/settings/organisation',
  customer: '/settings/organisation',
  supplier_portal: '/vendor/settings/organisation',
};

export function deriveSettingsScopeKind({
  isMSP,
  isViewingManagedCustomer,
  isSupplierPortal,
}: SettingsScopeSignals): SettingsScopeKind {
  // The supplier portal shell is its own home regardless of the underlying org's MSP signals.
  if (isSupplierPortal) {
    return 'supplier_portal';
  }
  if (isViewingManagedCustomer) {
    return 'managed_customer';
  }
  if (isMSP) {
    return 'msp_home';
  }
  return 'customer';
}

export function deriveSettingsScope(signals: SettingsScopeSignals): SettingsScope {
  const kind = deriveSettingsScopeKind(signals);
  return {
    kind,
    label: SCOPE_LABELS[kind],
    route: SCOPE_ROUTES[kind],
  };
}
